import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Check, ArrowRight, Rocket, Target, Map, Trophy, Zap } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Welcome() {
  const [step, setStep] = useState(1);
  const [career, setCareer] = useState(null);
  const [quests, setQuests] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();

  useEffect(() => {
    api.get("/dna").then(({ data }) => setCareer(data.top_match)).catch(() => {});
    api.get("/quests").then(({ data }) => setQuests(data)).catch(() => {});
  }, []);

  const genRoadmap = async () => {
    setLoading(true);
    try {
      await api.post("/roadmap/generate");
      setStep(3);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  const pickQuest = async (key) => {
    setLoading(true);
    try {
      await api.post("/quests/start", { quest_key: key });
      await refreshUser();
      toast.success("Quest started! Let's begin.");
      navigate("/app/home");
    } catch (e) { toast.error(apiError(e)); setLoading(false); }
  };

  const steps = [
    { icon: Target, title: "Confirm your target" },
    { icon: Map, title: "Generate roadmap" },
    { icon: Trophy, title: "Choose a quest" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-950 to-slate-950 text-white">
      <div className="mx-auto max-w-lg px-5 py-10">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-3xl bg-white/10"><Rocket className="h-8 w-8 text-amber-300" /></div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Welcome to NxT Plus</h1>
          <p className="mt-2 text-white/70">Let's build your career plan.</p>
        </motion.div>

        <div className="mt-8 flex items-center justify-center gap-2">
          {steps.map((s, i) => (
            <div key={i} className={`flex items-center gap-2 ${i + 1 <= step ? "text-amber-300" : "text-white/30"}`}>
              <span className={`flex h-8 w-8 items-center justify-center rounded-full border-2 ${i + 1 <= step ? "border-amber-300 bg-amber-300/10" : "border-white/20"}`}>
                {i + 1 < step ? <Check className="h-4 w-4" /> : i + 1}
              </span>
              {i < 2 && <span className="h-px w-6 bg-white/20" />}
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur">
          {step === 1 && (
            <div>
              <h2 className="font-display text-xl font-bold">Confirm your target career</h2>
              <div className="mt-4 rounded-2xl bg-white/10 p-5">
                <div className="text-xs font-bold uppercase tracking-widest text-white/50">Your #1 match</div>
                <div className="mt-1 font-display text-2xl font-extrabold">{career?.name || "—"}</div>
                <div className="font-mono text-amber-300">{career?.compatibility}% compatibility</div>
              </div>
              <button onClick={() => setStep(2)} data-testid="welcome-confirm-btn"
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-white py-3.5 font-bold text-slate-900 transition-all hover:bg-white/90">
                Yes, this is my target <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}
          {step === 2 && (
            <div>
              <h2 className="font-display text-xl font-bold">Generate your 90-day roadmap</h2>
              <p className="mt-2 text-sm text-white/70">A personalised plan to go from where you are to {career?.name}.</p>
              <button onClick={genRoadmap} disabled={loading} data-testid="welcome-roadmap-btn"
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-white py-3.5 font-bold text-slate-900 transition-all hover:bg-white/90 disabled:opacity-60">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Generate my roadmap <Zap className="h-4 w-4" /></>}
              </button>
            </div>
          )}
          {step === 3 && (
            <div>
              <h2 className="font-display text-xl font-bold">Choose your first Career Quest</h2>
              <div className="mt-4 space-y-2.5">
                {quests.slice(0, 4).map((q) => (
                  <button key={q.key} onClick={() => pickQuest(q.key)} disabled={loading} data-testid={`welcome-quest-${q.key}`}
                    className="flex w-full items-center justify-between rounded-2xl bg-white/10 p-4 text-left transition-colors hover:bg-white/20 disabled:opacity-60">
                    <div>
                      <div className="font-bold">{q.title}</div>
                      <div className="text-xs text-white/60">{q.duration_days} days · {q.total_levels} levels</div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-amber-300" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
